import {
  memo,
  MouseEvent,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Popover,
  Stack,
  Switch,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { useTranslation } from 'react-i18next';
import { NotificationUserItem } from './NotificationUserItem';
import { NotificationExportItem } from './NotificationExportItem';
import { NotificationEmpty } from '../NotificationEmpty';
import { useUserNotifications } from '../../hooks/useUserNotifications';
import { useNotificationCount } from '../../hooks/useNotificationCount';
import { useDeleteAllNotifications } from '../../hooks/useDeleteAllNotifications';
import { useMarkAsReadOnly } from '../../hooks/useMarkAsReadOnly';
// import { useMarkAllAsRead } from '../../hooks/useMarkAllAsRead';
import { NotificationTypeEnum } from 'modules/notification/constants/notification.enum';
import { useDeleteNotification } from 'modules/notification/hooks/useDeleteNotification';
import {
  NOTIFICATION_COUNT_KEY,
  NOTIFICATION_LIST_KEY,
  NOTIFICATION_USER_KEY,
} from 'modules/notification/constants/query';
import { FlexBox } from '@dfl/mui-react-common';
import { usePushNotifications } from 'modules/notification/hooks/usePushNotifications';
import toast from 'react-hot-toast';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';

interface NotificationDrawerProps {
  open: boolean;
  onClose: () => void;
  isSoundEnabled: boolean;
  onToggleSound: () => void;
}

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => {
  return (
    <div role='tabpanel' hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const NotificationDrawer = ({ open, onClose, isSoundEnabled, onToggleSound }: NotificationDrawerProps) => {
  const { t } = useTranslation('notification');
  const queryClient = useQueryClient();
  const [filterTab, setFilterTab] = useState(0);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState<HTMLElement | null>(null);
  const [visibleIds, setVisibleIds] = useState<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const { isSubscribed, enablePushNotifications, disablePushNotifications, isEnabling, isDisabling } =
    usePushNotifications();

  const { notifications, isLoading } = useUserNotifications({
    size: 100,
  });
  const { count } = useNotificationCount();
  const { mutate: deleteNotification, isPending } = useDeleteNotification({ showToast: false });
  const { mutate: deleteAllNotifications, isPending: isDeletingAll } = useDeleteAllNotifications({ showToast: false });
  const { mutate: markAsReadOnly } = useMarkAsReadOnly();
  // const { mutate: markAllAsRead, isPending: isMarkingAllAsRead } = useMarkAllAsRead();

  const handleMarkAsRead = useCallback(
    (id: string) => {
      markAsReadOnly({ fromId: id, toId: id });
    },
    [markAsReadOnly],
  );

  // Intersection observer to track visible notifications
  useLayoutEffect(() => {
    if (!open || !notifications?.length) {
      return;
    }

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      if (!containerRef.current) {
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          setVisibleIds((prev) => {
            const newSet = new Set(prev);
            entries.forEach((entry) => {
              const notificationId = entry.target.getAttribute('data-notification-id');

              if (!notificationId || notificationId === 'undefined') {
                return;
              }

              if (entry?.isIntersecting && entry?.intersectionRatio > 0) {
                newSet.add(notificationId);
              } else {
                newSet.delete(notificationId);
              }
            });
            return newSet;
          });
        },
        {
          root: containerRef.current,
          threshold: [0, 0.1, 0.5, 1.0],
          rootMargin: '0px',
        },
      );

      observerRef.current = observer;

      const items = containerRef.current.querySelectorAll('[data-notification-id]');

      items.forEach((item) => {
        observer.observe(item);
      });
    }, 500);

    return () => {
      clearTimeout(timeoutId);
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [notifications, filterTab, isPending, open]);

  // Mark as read after 3 seconds of opening the drawer if there are unread notifications in view
  useEffect(() => {
    if (!open || !notifications?.length) return;

    const timer = setTimeout(() => {
      // Only proceed if there are unread notifications in the viewport
      const unreadVisibleNotifications = notifications.filter((n) => n && !n.viewed && visibleIds.has(n._id));
      if (unreadVisibleNotifications.length > 0) {
        // Get the first and last unread notification IDs in the visible area
        const fromId = unreadVisibleNotifications[unreadVisibleNotifications.length - 1]._id;
        const toId = unreadVisibleNotifications[0]._id;

        // Mark the range as read and refresh the notifications list
        markAsReadOnly(
          { fromId, toId },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({
                queryKey: [NOTIFICATION_USER_KEY, NOTIFICATION_COUNT_KEY, NOTIFICATION_LIST_KEY],
              });
            },
          },
        );
      }
    }, 3000);

    return () => {
      clearTimeout(timer);
    };
  }, [open, notifications, markAsReadOnly, visibleIds, queryClient]);

  const userNotifications = useMemo(() => {
    return notifications.filter((n) => n.notificationKind === NotificationTypeEnum.notification);
  }, [notifications]);

  const exportNotifications = useMemo(() => {
    return notifications.filter((n) => n.notificationKind === NotificationTypeEnum.file_export);
  }, [notifications]);

  const handleFilterChange = useCallback((_event: SyntheticEvent, newValue: number) => {
    setFilterTab(newValue);
  }, []);

  const handleSettingsClick = (event: MouseEvent<HTMLElement>) => {
    setSettingsAnchorEl(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsAnchorEl(null);
  };

  const settingsOpen = Boolean(settingsAnchorEl);

  const theme = useTheme();

  // Push Notification
  const handleEnablePush = async () => {
    try {
      const registered = await enablePushNotifications();
      if (registered) {
        toast.success(t('pushNotificationsEnabled'));
      } else if (Notification.permission === 'denied') {
        toast.error(t('pushNotificationsBlocked'));
      } else {
        toast.error(t('pushNotificationsUnsupported'));
      }
    } catch (error) {
      console.error('Unable to enable push notifications', error);
      toast.error(t('pushNotificationsEnableError'));
    }
  };

  const handleDisablePush = async () => {
    try {
      await disablePushNotifications();
      toast.success(t('pushNotificationsDisabled'));
    } catch (error) {
      console.error('Unable to unsubscribe from push notifications', error);
      toast.error(t('pushNotificationsDisableError'));
    }
  };

  // const handleSendTestPush = async () => {
  //   try {
  //     const result = await PushNotificationService.sendTest();
  //     if (result.successCount > 0) {
  //       toast.success(t('testPushNotificationSent'));
  //     } else {
  //       toast.error(t('testPushNotificationError'));
  //     }
  //   } catch (error) {
  //     console.error('Unable to send test push notification', error);
  //     toast.error(t('testPushNotificationNotEnabled'));
  //   }
  // };

  return (
    <>
      <Drawer
        anchor='right'
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 500 },
            maxWidth: '100%',
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              background:
                theme.palette.mode === 'light'
                  ? `linear-gradient(128deg, ${theme.palette.primary.main}90 5%, ${theme.palette.primary.main} 100%)`
                  : `linear-gradient(178deg, ${theme.palette.primary.main}20, ${theme.palette.primary.main}20 100%)`,
              color: 'white',
              px: 3,
              py: 3,
              pb: 0,
            }}
          >
            <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2}>
              <Stack direction='row' alignItems='center' spacing={1}>
                <Typography variant='h5' fontWeight={600}>
                  {t('list')}
                </Typography>
                <Box
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    px: 1.5,
                    py: 0.5,
                  }}
                >
                  <Typography variant='body2' fontWeight={500}>
                    {count?.total}
                  </Typography>
                </Box>
              </Stack>

              <IconButton onClick={onClose} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Stack>

            <Stack direction='row' justifyContent='space-between' alignItems='center'>
              <Tabs
                value={filterTab}
                onChange={handleFilterChange}
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    minWidth: 'auto',
                    px: 2,
                    fontWeight: 500,
                    color: 'white !important',
                    pb: 0,
                  },
                }}
              >
                <Tab
                  label={
                    <FlexBox alignItems={'center'} gap={2}>
                      <Typography>{t('tabs.notifications')}</Typography>
                      {count?.notifications > 0 && <Badge badgeContent={count.notifications} color='primary' />}
                    </FlexBox>
                  }
                />

                <Tab
                  label={
                    <FlexBox alignItems={'center'} gap={2}>
                      <Typography>{t('tabs.exports')}</Typography>
                      {count?.exports > 0 && <Badge badgeContent={count.exports} color='primary' />}
                    </FlexBox>
                  }
                />
              </Tabs>
              <IconButton size='small' onClick={handleSettingsClick}>
                <SettingsOutlinedIcon fontSize='small' className='text-white' />
              </IconButton>
            </Stack>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto' }} ref={containerRef}>
            {/* Tab: Notifications */}
            <TabPanel value={filterTab} index={0}>
              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <Typography>Loading...</Typography>
                </Box>
              ) : !userNotifications?.length ? (
                <NotificationEmpty />
              ) : (
                <Box sx={{ opacity: isPending ? 0.6 : 1, transition: 'opacity 0.2s' }}>
                  {userNotifications.map((notification) => (
                    <NotificationUserItem
                      key={notification._id}
                      notification={notification}
                      onMarkAsRead={handleMarkAsRead}
                      onDelete={deleteNotification}
                    />
                  ))}
                </Box>
              )}
            </TabPanel>

            {/* Tab: Exports */}
            <TabPanel value={filterTab} index={1}>
              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <Typography>Loading...</Typography>
                </Box>
              ) : !exportNotifications?.length ? (
                <NotificationEmpty />
              ) : (
                <Box sx={{ opacity: isPending ? 0.6 : 1, transition: 'opacity 0.2s' }}>
                  {exportNotifications.map((notification) => (
                    <NotificationExportItem
                      key={notification._id}
                      notification={notification}
                      onMarkAsRead={handleMarkAsRead}
                      onDelete={deleteNotification}
                    />
                  ))}
                </Box>
              )}
            </TabPanel>
          </Box>
          <Divider />
          <Box sx={{ p: 2 }}>
            <Button
              variant='outlined'
              fullWidth
              onClick={() => {
                const notificationType =
                  filterTab === 0 ? NotificationTypeEnum.notification : NotificationTypeEnum.file_export;
                deleteAllNotifications(notificationType);
              }}
              disabled={isDeletingAll}
              sx={{ textTransform: 'none' }}
              color='error'
            >
              {t('actions.deleteAll')}
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Settings Popover */}
      <Popover
        open={settingsOpen}
        anchorEl={settingsAnchorEl}
        onClose={handleSettingsClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 2, minWidth: 250 }}>
          <Typography variant='subtitle2' fontWeight={600} mb={2}>
            {t('settings.title')}
          </Typography>
          <FormControlLabel
            control={<Switch checked={isSoundEnabled} onChange={onToggleSound} color='primary' />}
            label={
              <Stack direction='row' alignItems='center' spacing={1}>
                {isSoundEnabled ? (
                  <VolumeUpIcon fontSize='small' color='primary' />
                ) : (
                  <VolumeOffIcon fontSize='small' color='disabled' />
                )}
                <Typography variant='body2'>{t('settings.sound')}</Typography>
              </Stack>
            }
          />

          <Divider sx={{ my: 1 }} />

          {/* Push notifications setting */}
          <FormControlLabel
            control={
              <Switch
                checked={isSubscribed}
                disabled={isEnabling || isDisabling}
                onChange={isSubscribed ? handleDisablePush : handleEnablePush}
                color='primary'
              />
            }
            label={
              <Stack direction='row' alignItems='center' spacing={1}>
                {isSubscribed ? (
                  <NotificationsActiveIcon fontSize='small' color='primary' />
                ) : (
                  <NotificationsOffIcon fontSize='small' color='disabled' />
                )}
                <Typography variant='body2'>{t('settings.pushNotifications')}</Typography>
              </Stack>
            }
          />
        </Box>
      </Popover>
    </>
  );
};

export default memo(NotificationDrawer);
